/**
 * User Profile Service
 * Manages user preferences stored in Clerk metadata (theme and settings only)
 * Note: Boilerplates are NOT stored in user profile
 */

import { clerkService } from './clerkService';

const API_BASE_URL = import.meta.env?.VITE_API_URL || 'http://localhost:5001';

export class UserProfileService {
    constructor() {
        this.profile = null;
        this.loading = false;
    }

    // Helper to get auth headers
    async _getAuthHeaders() {
        const token = await clerkService.getToken();
        const headers = {
            'Content-Type': 'application/json'
        };
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        return headers;
    }

    /**
     * Get user profile from backend
     * Creates default profile if it doesn't exist
     */
    async getProfile() {
        if (this.loading) {
            // Wait for ongoing request
            while (this.loading) {
                await new Promise(resolve => setTimeout(resolve, 50));
            }
            return this.profile;
        }

        try {
            this.loading = true;
            const headers = await this._getAuthHeaders();

            const response = await fetch(`${API_BASE_URL}/api/user/profile`, {
                method: 'GET',
                headers,
                credentials: 'include'
            });

            if (!response.ok) {
                throw new Error(`Failed to get profile: ${response.statusText}`);
            }

            const data = await response.json();
            this.profile = data.profile;
            return this.profile;
        } catch (error) {
            console.error('Error getting user profile:', error);
            // Return default profile on error
            return {
                theme: 'classic',
                settings: {
                    claims: {
                        autoNumberSubitems: true,
                        autoUpdateDependencies: false
                    },
                    terms: {
                        autoRefresh: false
                    },
                    patentType: 'provisional',
                    jurisdiction: 'US'
                }
            };
        } finally {
            this.loading = false;
        }
    }

    /**
     * Update entire profile
     */
    async updateProfile(updates) {
        try {
            const headers = await this._getAuthHeaders();

            const response = await fetch(`${API_BASE_URL}/api/user/profile`, {
                method: 'PUT',
                headers,
                credentials: 'include',
                body: JSON.stringify(updates)
            });

            if (!response.ok) {
                throw new Error(`Failed to update profile: ${response.statusText}`);
            }

            const data = await response.json();
            this.profile = data.profile;
            return this.profile;
        } catch (error) {
            console.error('Error updating user profile:', error);
            throw error;
        }
    }

    /**
     * Update only theme preference
     */
    async updateTheme(theme) {
        try {
            const headers = await this._getAuthHeaders();

            const response = await fetch(`${API_BASE_URL}/api/user/profile/theme`, {
                method: 'PUT',
                headers,
                credentials: 'include',
                body: JSON.stringify({ theme })
            });

            if (!response.ok) {
                throw new Error(`Failed to update theme: ${response.statusText}`);
            }

            const data = await response.json();
            if (this.profile) {
                this.profile.theme = data.theme;
            }
            return data.theme;
        } catch (error) {
            console.error('Error updating theme:', error);
            throw error;
        }
    }

    /**
     * Update only settings
     */
    async updateSettings(settings) {
        try {
            const headers = await this._getAuthHeaders();

            const response = await fetch(`${API_BASE_URL}/api/user/profile/settings`, {
                method: 'PUT',
                headers,
                credentials: 'include',
                body: JSON.stringify({ settings })
            });

            if (!response.ok) {
                throw new Error(`Failed to update settings: ${response.statusText}`);
            }

            const data = await response.json();
            if (this.profile) {
                this.profile.settings = data.settings;
            }
            return data.settings;
        } catch (error) {
            console.error('Error updating settings:', error);
            throw error;
        }
    }

    /**
     * Clear cached profile (forces reload on next get)
     */
    clearCache() {
        this.profile = null;
    }
}

// Create singleton instance
export const userProfileService = new UserProfileService();
